import { useFormErrors } from "helpers/useFormErrors";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams, useNavigate, useLocation } from "react-router-dom";
import getSymbolFromCurrency from "currency-symbol-map";

import { useAppSelector, useAppDispatch } from "store/index";
import { ModalSegmentTabs, ModalSegmentRulesTabs } from "configs/types";
import { closeModal } from "store/modals/slice";
import {
  createSegment,
  getSegmentItem,
  deleteSegmentItem,
  updateSegment,
  handleClearFields,
  handleUpdateFields,
  updateSegmentPaymentsRules,
  updateSegmentBetRules,
  updateSegmentSessionRules,
  updateSegmentGamesRules,
  updateSegmentPlatformRules,
} from "store/segments/slice";
import { PlayerItem } from "store/players/list/slice";
import { OutNotificationItem } from "store/outAppNotifications/slice";
import { NotificationTypes, showNotification } from "store/notifications/slice";
import { apiRoutes } from "configs/apiRoutes";
import instance from "api/apiClient";

import Button from "components/buttons/Button";
import InnerInput from "components/inputs/InnerInput/InnerInput";
import SwitchBtn from "components/buttons/SwitchBtn";
import SearchInput from "components/inputs/SearchInput/SearchInput";
import RiskScoreCell from "components/table/components/RiskScoreCell/RiskScoreCell";

import SegmentRulesPaymentsTab from "../tabs/segment/SegmentRulesPaymentsTab";
import SegmentRulesBetTab from "../tabs/segment/SegmentRulesBetTab";
import SegmentRulesActivityTab from "../tabs/segment/SegmentRulesActivityTab";
import RulesGamesForm from "../tabs/segment/SegmentRulesGamesTab";
import SegmentRulesPlatformTab from "../tabs/segment/SegmentRulesPlatformTab";

import { Modal } from "components/Modal/Modal";
import styles from "./PlayersSegmentEditModal.module.scss";
import { selectFilteredGeo } from "store/geoFilter/selectors";
import ModalSkeleton, {
  ModalFooterSkeleton,
} from "components/global/Skeleton/ModalSkeleton/ModalSkeleton";
import ModalTabSkeleton from "components/global/Skeleton/ModalTabSkeleton/ModalTabSkeleton";
import { usePermissionHelper } from "helpers/usePermissionHelper";
import { handleApiError } from "helpers/handleApiError";
import { SegmentNotificationsTab } from "./tabs/SegmentNotificationsTab";

const PlayersSegmentEditModal: React.FC = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const permissions = usePermissionHelper();
  const entityId = searchParams.get("entityId");
  const [isCreateFlow] = useState(entityId === null);
  const canRead = permissions?.read ?? false;
  const canEdit = isCreateFlow
    ? (permissions?.create ?? false)
    : (permissions?.update ?? false);
  const canDelete = permissions?.delete ?? false;

  const itemPayload = useAppSelector((store) => store.segments.payload);
  const itemFetched = useAppSelector((store) => store.segments.isItemFetched);
  const isItemFetching = useAppSelector(
    (store) => store.segments.isItemFetching,
  );
  const geoList = useAppSelector(selectFilteredGeo);
  const currencies = useAppSelector((store) => store.settings.currencies);

  const [playersList, setPlayersList] = useState<PlayerItem[]>([]);
  const [playersListFull, setPlayersListFull] = useState<PlayerItem[]>([]);
  const [searchValue, setSearchValue] = useState<string>("");
  const [selectedItem, setSelectedItem] = useState<string>();
  const options = [
    {
      value: "auto",
      label: t("auto"),
    },
    {
      value: "manual",
      label: t("manual"),
    },
  ];
  const [tabs, setTabs] = useState<ModalSegmentTabs[]>([
    ModalSegmentTabs.infoUser,
    ModalSegmentTabs.players,
    // ModalSegmentTabs.settings,
  ]);
  const [activeTab, setActiveTab] = useState<ModalSegmentTabs>(
    ModalSegmentTabs.infoUser,
  );
  const formErrors = useFormErrors();
  const [tabsRules] = useState<ModalSegmentRulesTabs[]>([
    ModalSegmentRulesTabs.payments,
    ModalSegmentRulesTabs.bets,
    ModalSegmentRulesTabs.activity,
    ModalSegmentRulesTabs.games,
    ModalSegmentRulesTabs.platform,
  ]);
  const [activeTabRules, setActiveTabRules] = useState<ModalSegmentRulesTabs>(
    ModalSegmentRulesTabs.payments,
  );
  const [isEdit, setIsEdit] = useState<boolean>(false);
  const [deleteValue, setDeleteValue] = useState<string>("");
  const [isFetched, setFetched] = useState<boolean>(false);
  const [activeGeo, setActiveGeo] = useState<string>(geoList[0] ?? "");
  const [activeCurrency, setActiveCurrency] = useState<string>(
    currencies[0] ?? "",
  );
  const [currency, setCurrency] = useState("EUR");
  useEffect(() => {
    formErrors.resetErrors();
  }, [currency, activeTabRules]);
  const [selectedNotification, setSelectedNotification] = useState<
    OutNotificationItem | undefined
  >();
  const [isConfirmingDispatch, setIsConfirmingDispatch] = useState(false);
  const [isDispatching, setIsDispatching] = useState(false);
  const [isNotificationsLoading, setIsNotificationsLoading] = useState(false);
  const [isPaymentProvidersLoading, setIsPaymentProvidersLoading] =
    useState(false);

  const paymentPayload = useAppSelector(
    (store) =>
      store.segments.payload?.paymentRules?.filter(
        (el) => el.currency === currency,
      )[0],
  );
  const betPayload = useAppSelector(
    (store) =>
      store.segments.payload?.betRules?.filter(
        (el) => el.currency === currency,
      )[0],
  );
  const sessionPayload = useAppSelector(
    (store) => store.segments.payload?.sessionRules,
  );
  const gamesPayload = useAppSelector(
    (store) => store.segments.payload?.gameRules,
  );
  const platformPayload = useAppSelector(
    (store) => store.segments.payload?.platformRules,
  );

  const segmentType = location.pathname.includes("auto") ? "auto" : "manual";

  useEffect(() => {
    if (itemPayload?.type === "auto") {
      setTabs([
        ModalSegmentTabs.infoUser,
        ModalSegmentTabs.rules,
        ModalSegmentTabs.players,
        // ModalSegmentTabs.settings,
      ]);
    } else {
      setTabs([
        ModalSegmentTabs.infoUser,
        ModalSegmentTabs.players,
        ModalSegmentTabs.notifications,
        // ModalSegmentTabs.settings,
      ]);
    }
  }, [itemPayload]);

  const handleLoadPlayers = async () => {
    setFetched(false);
    try {
      const result = await instance.get(
        `${apiRoutes.segmentUsersList}/${itemPayload?.id}?limit=1000&offset=0&geo=${activeGeo}${itemPayload?.type === "auto" ? `&currency=${activeCurrency}` : ""}`,
      );
      setPlayersListFull(result.data.items);
    } catch {
      console.log("handleLoadPlayers error");
    }
    setFetched(true);
  };

  const segmentId = itemPayload?.id;

  useEffect(() => {
    if (canRead && segmentId) handleLoadPlayers();
  }, [canRead, segmentId, activeGeo, activeCurrency]);

  useEffect(() => {
    const query = searchValue.trim().toLowerCase();

    if (!query) {
      setPlayersList(playersListFull);
      return;
    }

    setPlayersList(
      playersListFull.filter((player) =>
        [
          player.id,
          player.fullName,
          player.firstName,
          player.lastName,
          player.nickname,
          player.email,
        ].some((value) =>
          String(value ?? "")
            .toLowerCase()
            .includes(query),
        ),
      ),
    );
  }, [playersListFull, searchValue]);

  useEffect(() => {
    if (!isEdit) {
      setSelectedItem(segmentType);
      handleData("type", segmentType);
      handleData("isActive", 0);
    } else if (itemPayload?.type) {
      setSelectedItem(itemPayload.type);
    }
  }, [isEdit, itemPayload?.type]);

  useEffect(() => {
    if (canRead && searchParams.get("entityId") !== null) {
      setIsEdit(true);
      if (searchParams.get("tab") !== null) {
        setActiveTab(searchParams.get("tab") as ModalSegmentTabs);
      }
      const data = { id: searchParams.get("entityId") ?? 1, currencies };
      dispatch(getSegmentItem(data));
    } else {
      setIsEdit(false);
    }
  }, [canRead, searchParams, currencies]);

  const isLoaded = () => {
    if (searchParams.get("entityId") !== null && !itemFetched) {
      return false;
    }

    return true;
  };

  const handleTabClick = (tab: ModalSegmentTabs) => {
    if (!isEdit && tab !== ModalSegmentTabs.infoUser) {
      dispatch(
        showNotification({
          title: t("warning"),
          message: t("createEntityFirst"),
          type: NotificationTypes.warning,
        }),
      );

      return;
    }

    setIsConfirmingDispatch(false);
    setIsNotificationsLoading(tab === ModalSegmentTabs.notifications);
    setActiveTab(tab);
  };

  const handleCloseClick = () => {
    navigate(`${location.pathname}`);
    dispatch(closeModal());
    dispatch(handleClearFields());
  };

  const handleGetPayload = () => {
    return itemPayload?.title;
  };

  const handleDeleteClick = async () => {
    if (!canDelete) return;

    const result = await dispatch(deleteSegmentItem(itemPayload?.id ?? 0));
    if (result.payload === true) {
      dispatch(
        showNotification({
          title: t("success"),
          message: t("entityDeleteSuccess"),
          type: NotificationTypes.success,
        }),
      );
    } else {
      dispatch(
        showNotification({
          title: t("error"),
          message: t("entityDeleteError"),
          type: NotificationTypes.error,
        }),
      );
    }
    handleCloseClick();
  };

  const handleData = (key: string, value: string | boolean | number) => {
    if (!canEdit) return;

    // @ts-ignore
    dispatch(handleUpdateFields({ [key]: value }));
  };

  const onSearch = (e: string) => {
    setSearchValue(e);
  };

  const renderRulesTab = () => {
    if (activeTabRules === ModalSegmentRulesTabs.payments) {
      return (
          <SegmentRulesPaymentsTab fieldErrors={formErrors.errors} clearFieldError={formErrors.clearFieldError}
          currency={currency}
          setCurrency={setCurrency}
          onLoadingChange={setIsPaymentProvidersLoading}
        />
      );
    }
    if (activeTabRules === ModalSegmentRulesTabs.bets) {
      return (
        <SegmentRulesBetTab fieldErrors={formErrors.errors} clearFieldError={formErrors.clearFieldError} currency={currency} setCurrency={setCurrency} />
      );
    }
    if (activeTabRules === ModalSegmentRulesTabs.activity) {
      return <SegmentRulesActivityTab fieldErrors={formErrors.errors} clearFieldError={formErrors.clearFieldError} />;
    }
    if (activeTabRules === ModalSegmentRulesTabs.games) {
      return <RulesGamesForm />;
    }
    if (activeTabRules === ModalSegmentRulesTabs.platform) {
      return <SegmentRulesPlatformTab />;
    }
    return null;
  };
  const renderTab = () => {
    if (activeTab === ModalSegmentTabs.rules) {
      return (
        <div className="segment-rules-wrapper">
          <div className="game-modal-tabs segment-tabs small">
            {tabsRules.map((el) => (
              <div
                className={`tab-item ${activeTabRules === el ? "active" : ""}`}
                key={el}
                onClick={() => {
                  setIsPaymentProvidersLoading(
                    el === ModalSegmentRulesTabs.payments,
                  );
                  setActiveTabRules(el);
                }}
              >
                {t(el)}
              </div>
            ))}
          </div>
          <div className={!canEdit ? styles.readOnlyRules : undefined}>
            {renderRulesTab()}
          </div>
        </div>
      );
    }
    if (activeTab === ModalSegmentTabs.infoUser) {
      return (
        <div className="segment-form-wrapper">
          <div className="segment-form-content">
            <div className="segment-form-main-row">
              <div className="segment-form-box--item">
                <InnerInput
                  value={itemPayload?.title ?? ""}
                  placeholder={t("enterTitle")}
                  label={t("title")}
                  type="text"
                  id="game-title"
                  onChange={(e) => handleData("title", e)}
                  disabled={!canEdit}
                />
              </div>
              <div className="segment-form-box--item">
                <InnerInput
                  value={selectedItem === "auto" ? t("auto") : t("manual")}
                  label={t("type")}
                  type="text"
                  id="segment-type"
                  onChange={() => {}}
                  disabled
                />
              </div>
              <div className="segment-form-box--item segment-form-status">
                <SwitchBtn
                  activeTxt={t("active")}
                  notActiveTxt={t("hidden")}
                  isActive={!!itemPayload?.isActive}
                  handleClick={() =>
                    handleData("isActive", !!itemPayload?.isActive ? 0 : 1)
                  }
                  disabled={!canEdit}
                />
              </div>
            </div>
            <div className="segment-form-box--item">
              <InnerInput
                maxCodePoints={2500}
                value={itemPayload?.description ?? ""}
                placeholder={t("enterText")}
                label={t("description")}
                type="textarea"
                id="game-description"
                onChange={(e) => handleData("description", e)}
                disabled={!canEdit}
              />
            </div>
          </div>
        </div>
      );
    }
    if (activeTab === ModalSegmentTabs.players) {
      return (
        <div className="segment-form-wrapper players-list">
          <div className="segment-form-content">
            <div className="game-modal-tabs small">
              {geoList?.map((el) => (
                <div
                  className={`tab-item ${activeGeo.includes(el) ? "active" : ""}`}
                  key={el}
                  onClick={() => {
                    setActiveGeo(el);
                  }}
                >
                  {el.toLowerCase() === "ww" ? (
                    <span className="mr-5">🌎</span>
                  ) : (
                    <img
                      src={`https://flagcdn.com/w40/${el.toLowerCase()}.png`}
                      alt={el}
                    />
                  )}
                  {t(el)}
                </div>
              ))}
            </div>
            {itemPayload?.type === "auto" && (
              <div className="game-modal-tabs small">
                {currencies?.map((el) => (
                  <div
                    className={`tab-item ${activeCurrency.includes(el) ? "active" : ""}`}
                    key={el}
                    onClick={() => {
                      setActiveCurrency(el);
                    }}
                  >
                    <div className="currency-tab-item">
                      <div className="currency">
                        {getSymbolFromCurrency(el)}
                      </div>{" "}
                      {el}
                    </div>
                  </div>
                ))}
              </div>
            )}
            <div className="games-order-search">
              <SearchInput
                placeholder={t("searchPlayer")}
                value={searchValue}
                onChange={onSearch}
                isSmall
              />
            </div>
            <div className="games-order-list segments-order-list">
              {isFetched ? (
                <>
                  <div className="item">
                    <div className="id">ID</div>
                    <div className="name">{t("name").toUpperCase()}</div>
                    <div className="email">{t("email").toUpperCase()}</div>
                    <div className="risk">{t("risk").toUpperCase()}</div>
                    <div className="category">
                      {t("category").toUpperCase()}
                    </div>
                  </div>
                  {playersList.length === 0 ? (
                    <div className={styles.playersEmpty}>
                      {t("noPlayersFoundShort")}
                    </div>
                  ) : (
                    playersList.map((el) => (
                      <div className={`item`} key={el.id}>
                        <div className="id">{el.id}</div>
                        <div className="name">
                          {el.fullName ||
                            [el.firstName, el.lastName]
                              .filter(Boolean)
                              .join(" ") ||
                            el.nickname ||
                            "—"}
                        </div>
                        <div className="email">{el.email}</div>
                        <div className="risk">
                          <RiskScoreCell
                            riskScore={
                              el.riskScore ?? { value: null, type: "none" }
                            }
                          />
                        </div>
                        <div className="category">{el.category}</div>
                      </div>
                    ))
                  )}
                </>
              ) : (
                <ModalTabSkeleton />
              )}
            </div>
          </div>
        </div>
      );
    }
    if (activeTab === ModalSegmentTabs.notifications) {
      return (
        <SegmentNotificationsTab
          selectedNotification={selectedNotification}
          onSelectionChange={setSelectedNotification}
          onLoadingChange={setIsNotificationsLoading}
        />
      );
    }
    return (
      <div className="game-form-wrapper">
        <div className="game-form-content delete">
          <div className="game-form-box">
            <div className="game-form-box--item">
              <InnerInput
                value={deleteValue}
                placeholder={t("enterEntityTitleToDelete")}
                label={t("areYouSureYouWantDeleteThisEntity")}
                type="text"
                id="game-title"
                onChange={(e) => setDeleteValue(e)}
              />
              <Button
                isDelete
                txt={t("delete")}
                handleClick={handleDeleteClick}
                disabled={deleteValue !== handleGetPayload()}
              />
            </div>
          </div>
        </div>
      </div>
    );
  };

  const handleApply = async () => {
    formErrors.resetErrors();
    if (!canEdit) return;

    if (activeTab === ModalSegmentTabs.notifications) {
      if (selectedNotification) setIsConfirmingDispatch(true);
      return;
    }

    if (activeTab === ModalSegmentTabs.rules) {
      const numericRuleAction =
        activeTabRules === ModalSegmentRulesTabs.payments && paymentPayload
          ? updateSegmentPaymentsRules({ ...paymentPayload, segmentId: Number(entityId) })
          : activeTabRules === ModalSegmentRulesTabs.bets && betPayload
            ? updateSegmentBetRules({ ...betPayload, segmentId: Number(entityId) })
            : activeTabRules === ModalSegmentRulesTabs.activity && sessionPayload
              ? updateSegmentSessionRules({ ...sessionPayload, segmentId: Number(entityId) })
              : null;
      if (numericRuleAction) {
        try {
          await dispatch(numericRuleAction).unwrap();
          dispatch(showNotification({ title: t("success"), message: t("entityUpdateSuccess"), type: NotificationTypes.success }));
          if (!isEdit && activeTabRules !== ModalSegmentRulesTabs.payments) {
            handleCloseClick();
          }
        } catch (error) {
          handleApiError({ error, t, dispatch, formErrors });
        }
        return;
      }
      if (activeTabRules === ModalSegmentRulesTabs.games && gamesPayload) {
        const result = await dispatch(
          updateSegmentGamesRules({
            ...gamesPayload,
            segmentId: parseInt(searchParams.get("entityId") ?? "0") ?? 0,
          }),
        );
        if (result.payload === true) {
          dispatch(
            showNotification({
              title: t("success"),
              message: t("entityUpdateSuccess"),
              type: NotificationTypes.success,
            }),
          );
          if (!isEdit) {
            handleCloseClick();
          }
        } else {
          dispatch(
            showNotification({
              title: t("error"),
              message: t("entityUpdateError"),
              type: NotificationTypes.error,
            }),
          );
        }
      }
      if (
        activeTabRules === ModalSegmentRulesTabs.platform &&
        platformPayload
      ) {
        const result = await dispatch(
          updateSegmentPlatformRules({
            ...platformPayload,
            segmentId: parseInt(searchParams.get("entityId") ?? "0") ?? 0,
          }),
        );
        if (result.payload === true) {
          dispatch(
            showNotification({
              title: t("success"),
              message: t("entityUpdateSuccess"),
              type: NotificationTypes.success,
            }),
          );
          if (!isEdit) {
            handleCloseClick();
          }
        } else {
          dispatch(
            showNotification({
              title: t("error"),
              message: t("entityUpdateError"),
              type: NotificationTypes.error,
            }),
          );
        }
      }
    }
    if (itemPayload && activeTab === ModalSegmentTabs.infoUser) {
      try {
        if (isEdit) {
          const result = await dispatch(updateSegment(itemPayload)).unwrap();

          if (result === true) {
            dispatch(
              showNotification({
                title: t("success"),
                message: t("entityUpdateSuccess"),
                type: NotificationTypes.success,
              }),
            );
          }
        } else {
          const result = await dispatch(createSegment(itemPayload)).unwrap();

          dispatch(
            showNotification({
              title: t("success"),
              message: t("entityCreateSuccess"),
              type: NotificationTypes.success,
            }),
          );

          navigate(
            `${location.pathname}?activeModal=segment&entityId=${result}`,
          );
        }
      } catch (error) {
        dispatch(
          showNotification({
            title: t("error"),
            message: isEdit ? t("entityUpdateError") : t("entityCreateError"),
            type: NotificationTypes.error,
          }),
        );
      }
    }
  };

  const handleNotificationDispatch = async () => {
    const segmentId = Number(entityId ?? itemPayload?.id);

    if (!segmentId || !selectedNotification?.id) return;

    setIsDispatching(true);

    try {
      await instance.post(apiRoutes.notificationManualDispatch, {
        notificationId: selectedNotification.id,
        segmentIds: [segmentId],
      });

      setIsConfirmingDispatch(false);
      setSelectedNotification(undefined);
      dispatch(
        showNotification({
          title: t("success"),
          message: t("notifSendSuccess"),
          type: NotificationTypes.success,
        }),
      );
    } catch (error) {
      setIsConfirmingDispatch(false);
      handleApiError({ error, t, dispatch });
    } finally {
      setIsDispatching(false);
    }
  };

  const isDisabled = () => {
    if (
      !!itemPayload?.description &&
      !!itemPayload.type &&
      !!itemPayload.title
    ) {
      return false;
    }
    return true;
  };

  return (
    <Modal onClose={handleCloseClick}>
      <Modal.Header>
        {segmentType === "auto" ? t("autoSegment") : t("manualSegment")}
      </Modal.Header>

      {!isLoaded() ? (
        <ModalSkeleton />
      ) : (
        <>
          {!isConfirmingDispatch && (
            <Modal.Tabs
              tabs={tabs}
              activeTab={activeTab}
              onClick={handleTabClick}
              isSegmentTabs
            />
          )}

          <Modal.Content
            className={`${styles.playersSegmentContentWrapper} ${
              isConfirmingDispatch
                ? styles.confirmationContent
                : activeTab === ModalSegmentTabs.players
                  ? styles.playersTabContent
                  : activeTab === ModalSegmentTabs.notifications
                    ? styles.notificationsTabContent
                    : ""
            }`}
          >
            {isConfirmingDispatch ? (
              <div className={styles.confirmation}>
                <h3>{t("confirmNotificationDispatch")}</h3>
                <p>
                  {t("manualNotificationDispatchConfirmation", {
                    title: selectedNotification?.title ?? "",
                    count: 1,
                  })}
                </p>
                <dl>
                  <div>
                    <dt>{t("notification")}</dt>
                    <dd>{selectedNotification?.title}</dd>
                  </div>
                  <div>
                    <dt>{t("segment")}</dt>
                    <dd>{itemPayload?.title}</dd>
                  </div>
                </dl>
              </div>
            ) : (
              renderTab()
            )}
          </Modal.Content>

          {(canEdit || canDelete) && (
            <Modal.Footer>
              {isConfirmingDispatch ? (
                <>
                  <Button
                    isSecondary
                    txt={t("back")}
                    handleClick={() => setIsConfirmingDispatch(false)}
                    disabled={isDispatching}
                  />
                  <Button
                    isGreen
                    className={styles.primaryAction}
                    txt={t("confirm")}
                    handleClick={handleNotificationDispatch}
                    isLoad={isDispatching}
                  />
                </>
              ) :
                (activeTab === ModalSegmentTabs.players && !isFetched) ||
                  (activeTab === ModalSegmentTabs.notifications &&
                    isNotificationsLoading) ||
                  (activeTab === ModalSegmentTabs.rules &&
                    activeTabRules === ModalSegmentRulesTabs.payments &&
                    isPaymentProvidersLoading) ? (
                <ModalFooterSkeleton />
              ) : (
                <>
                  <Button
                    isSecondary
                    txt={t("cancel")}
                    handleClick={handleCloseClick}
                  />
                  {canEdit && (
                    <Button
                      className={styles.primaryAction}
                      isPrimary={activeTab !== ModalSegmentTabs.notifications}
                      isGreen={activeTab === ModalSegmentTabs.notifications}
                      isLoad={isItemFetching}
                      handleClick={handleApply}
                      txt={
                        activeTab === ModalSegmentTabs.notifications
                          ? t("send")
                          : t("save")
                      }
                      disabled={
                        activeTab === ModalSegmentTabs.notifications
                          ? !selectedNotification
                          : isDisabled()
                      }
                    />
                  )}
                </>
              )}
            </Modal.Footer>
          )}
        </>
      )}
    </Modal>
  );
};

export default PlayersSegmentEditModal;
